import { HStack, Icon, IconButton, IconProps } from '@chakra-ui/react';
import { LIST_FEATURE_FLAGS, useFeatureFlag } from '@diamond/shared/utils';

interface ActionButtonProps {
  onClickDelete?: () => void;
  onClickEdit?: () => void;
  editIcon?: React.ReactElement;
  deleteIcon?: React.ReactElement;
  onClickDuplicate?: () => void;
  duplicateIcon?: React.ReactElement;
}

const DefaultEditIcon = (props: IconProps) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path
      fill="currentColor"
      d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm14.71-9.04c.39-.39.39-1.02 0-1.41l-1.5-1.5a.9959.9959 0 0 0-1.41 0l-1.13 1.13 3.75 3.75 1.29-1.47z"
    />
  </Icon>
);

const DefaultDeleteIcon = (props: IconProps) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path
      fill="currentColor"
      d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1z"
    />
  </Icon>
);

const DefaultDuplicateIcon = (props: IconProps) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path
      fill="currentColor"
      d="M12.75 10.75H14.25V8.75H16.25V7.25H14.25V5.25H12.75V7.25H10.75V8.75H12.75V10.75ZM10.75 14.75H16.25V13.25H10.75V14.75ZM8.30775 18.5C7.80258 18.5 7.375 18.325 7.025 17.975C6.675 17.625 6.5 17.1974 6.5 16.6923V3.30775C6.5 2.80258 6.675 2.375 7.025 2.025C7.375 1.675 7.80258 1.5 8.30775 1.5H15.25L20.5 6.75V16.6923C20.5 17.1974 20.325 17.625 19.975 17.975C19.625 18.325 19.1974 18.5 18.6923 18.5H8.30775ZM8.30775 17H18.6923C18.7693 17 18.8398 16.9679 18.9038 16.9038C18.9679 16.8398 19 16.7692 19 16.6923V7.5L14.5 3H8.30775C8.23075 3 8.16025 3.03208 8.09625 3.09625C8.03208 3.16025 8 3.23075 8 3.30775V16.6923C8 16.7692 8.03208 16.8398 8.09625 16.9038C8.16025 16.9679 8.23075 17 8.30775 17ZM4.30775 22.5C3.80258 22.5 3.375 22.325 3.025 21.975C2.675 21.625 2.5 21.1974 2.5 20.6923V7.5H4V20.6923C4 20.7693 4.03208 20.8398 4.09625 20.9038C4.16025 20.9679 4.23075 21 4.30775 21H14.5V22.5H4.30775Z"
    />
  </Icon>
);

export function ActionButton({
  onClickDelete,
  onClickEdit,
  editIcon,
  deleteIcon,
  onClickDuplicate,
  duplicateIcon,
}: ActionButtonProps) {
  const isNewCg3 = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_HIGHLIGHT_CG3_NEW,
    false
  );

  const handleDelete = () => {
    if (onClickDelete) {
      onClickDelete();
    }
  };

  const handleEdit = () => {
    if (onClickEdit) {
      onClickEdit();
    }
  };

  const handleDuplicate = () => {
    if (onClickDuplicate) {
      onClickDuplicate();
    }
  };
  return (
    <HStack display="flex" justifyContent="flex-end">
      {onClickEdit && (
        <IconButton
          size="sm"
          aria-label="edit-button"
          color="white"
          bgColor="blue"
          border="none"
          _hover={{ bgColor: 'azure' }}
          _active={{ bgColor: 'blue' }}
          icon={editIcon ?? <DefaultEditIcon />}
          onClick={() => handleEdit()}
        />
      )}
      {onClickDuplicate && isNewCg3 && (
        <IconButton
          size="sm"
          aria-label="duplicate-button"
          color="white"
          bgColor="purple"
          border="none"
          _hover={{ bgColor: 'purple.400' }}
          _active={{ bgColor: 'purple' }}
          icon={duplicateIcon ?? <DefaultDuplicateIcon />}
          onClick={() => handleDuplicate()}
        />
      )}
      {onClickDelete && (
        <IconButton
          size="sm"
          aria-label="delete-button"
          color="white"
          bgColor="red"
          border="none"
          _hover={{ bgColor: 'pink' }}
          _active={{ bgColor: 'red' }}
          icon={deleteIcon ?? <DefaultDeleteIcon />}
          onClick={() => handleDelete()}
        />
      )}
    </HStack>
  );
}

export default ActionButton;
